import { Loader2 } from 'lucide-react';
import type { FC, ReactNode, DragEvent, ChangeEvent } from 'react';
import { useCallback, useRef } from 'react';

interface DropZoneProps {
  className?: string;
  onUpload: (file: File) => void;
  onUploadMultiple?: (files: File[]) => void;
  onError?: (error: string, file: File) => void;
  accept?: string;
  maxSizeMB?: number;
  disabled?: boolean;
  loading?: boolean;
  multiple?: boolean;
  children: ReactNode;
}

export const DropZone: FC<DropZoneProps> = ({
  className = '',
  onUpload,
  onUploadMultiple,
  onError,
  accept,
  maxSizeMB = 10,
  disabled = false,
  loading = false,
  multiple = false,
  children,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(
    (file: File): boolean => {
      if (accept) {
        const allowedExtensions = accept.split(',').map((ext) => ext.trim().toLowerCase());
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
          const allowedFormats = allowedExtensions.join(', ').toUpperCase();
          onError?.(`Invalid file type. Only ${allowedFormats} files are allowed.`, file);
          return false;
        }
      }

      if (file.size > maxSizeMB * 1024 * 1024) {
        onError?.(`File too large. Maximum size is ${maxSizeMB}MB.`, file);
        return false;
      }

      return true;
    },
    [accept, maxSizeMB, onError]
  );

  const handleFile = useCallback(
    (files: FileList | null) => {
      if (disabled || !files || files.length === 0) return;

      const selected = (multiple ? Array.from(files) : [files[0]]).filter(validateFile);

      if (selected.length > 0) {
        if (multiple && onUploadMultiple) {
          onUploadMultiple(selected);
        } else {
          for (const file of selected) {
            onUpload(file);
          }
        }
      }

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onUpload, onUploadMultiple, disabled, multiple, validateFile]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (disabled) return;
      handleFile(e.dataTransfer.files);
    },
    [disabled, handleFile]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleFile(e.target.files);
    },
    [handleFile]
  );

  const rootClassName = [
    'relative flex flex-col items-center gap-2 rounded-md! border-2 border-dashed p-4 transition',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    'border-[--neutral-white-NW800]',
    className,
  ].join(' ');

  return (
    <div className={rootClassName} onDrop={handleDrop}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={handleInputChange}
        className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
      />

      {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : children}
    </div>
  );
};
